import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useGoBack } from '@/lib/goBack';
import { useBackStep } from '@/lib/backStep';
import { isMessage, previewOf, useChats } from '@/data/store';
import type { Chat } from '@/data/types';
import { useAuth } from '@/lib/auth';
import { formatPhone, formatRand, loadChatContext, openInSentinel, portalUrl, sentinelPathFor, type ChatContext, type ReplyWait } from '@/lib/contact';
import { sampleContext, sampleReplies } from '@/data/sample';
import { Avatar } from '@/components/Avatar';
import { Switch } from '@/components/Switch';
import { ArchiveIcon, ArrowDownIcon, BackIcon, SearchIcon, StarIcon, UnarchiveIcon, PersonIcon, CalendarIcon, ClockIcon, BellIcon, BellOffIcon, TagIcon, MailIcon } from '@/components/Icons';
import { SheetOption } from '@/components/Overlay';
import { LabelLine, LabelSheet } from '@/components/Labels';
import { useOverlay } from '@/components/Overlay';
import { DrcLive } from '@/components/DrcLive';
import { CustomerPanel } from '@/components/records/CustomerPanel';
import { BookingsPanel } from '@/components/records/BookingsPanel';
import { HistoryPanel } from '@/components/records/HistoryPanel';
import { RecordBack, type RecordStep } from '@/components/records/RecordPanel';
import { day } from '@/components/records/BookingsPanel';
import { sampleRecords } from '@/components/records/sample';
import { liveRecords, rightsFor, type RecBooking, type RecCustomer } from '@/lib/records';
import { JUNK_REASONS, markJunk, type JunkReason } from '@/lib/junk';
import { AiLeo, AiStaff } from '@/components/AiLeo';
import { STAFF_ASSISTANT_NAME } from '@/lib/assistant';
import { StatusChip, handoverWords } from '@/components/ChatRow';
import { ReminderSheet } from '@/components/ReminderSheet';
import { PosLeadSheet } from '@/components/PosLeadSheet';
import { reminderFor, shortDate, TYPE_WORDS, useReminders } from '@/lib/reminders';

// Tap the chat header (or beside the chat on a computer): the customer's Info. Picture, name
// and number, then the enquiry, who handles it and what Sentinel knows (moved here from chips
// and banners, Gary 15 Oct), then Ask Dr C, Search, Mute, media, starred messages and archive.
// WhatsApp's own profile photo, About and status are not available through Meta's API,
// so the picture is our initials avatar.


/** Junk on a lead chat (Gary, 19 Oct): the tracker's reasons, one tap each. */
function JunkSheet({ chatId, leadId, onDone }: { chatId: string; leadId: string | null; onDone: (message: string) => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pick = (reason: JunkReason) => {
    setBusy(true); setError(null);
    markJunk(chatId, leadId, reason).then(() => onDone(`Junk: ${reason}. Chat closed.`))
      .catch((e: unknown) => { setError(e instanceof Error ? e.message : 'That did not save. Try again.'); setBusy(false); });
  };
  return (
    <div>
      <h2 className="chats-sheet-title">Why is this junk?</h2>
      <p className="chats-sheet-hint">Not booked yet isn't junk. The lead is closed and the chat too.</p>
      {error && <p className="chats-rec-error" role="alert">{error}</p>}
      {JUNK_REASONS.map((r) => (
        <button key={r} type="button" className="chats-set" disabled={busy} onClick={() => pick(r)}>
          <div className="chats-set-main">{r}</div>
        </button>
      ))}
    </div>
  );
}

function SearchSheet({ chat, onOpen }: { chat: Chat; onOpen: (messageId: string) => void }) {
  const [q, setQ] = useState('');
  const term = q.trim().toLowerCase();
  const hits = term.length < 2
    ? []
    : chat.thread.filter(isMessage).filter((m) => previewOf(m.body).toLowerCase().includes(term)).slice(-30).reverse();
  return (
    <div className="chats-contact-search">
      <h2 className="chats-sheet-title">Search in chat</h2>
      <input className="chats-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search" aria-label="Search in chat" autoFocus />
      {term.length >= 2 && hits.length === 0 && <p className="chats-contact-note">No messages found.</p>}
      {hits.map((m) => (
        <button key={m.id} type="button" className="chats-set" onClick={() => onOpen(m.id)}>
          <div className="chats-set-main">{previewOf(m.body)}<small>{m.at}</small></div>
        </button>
      ))}
    </div>
  );
}

export function ContactScreen() {
  const { id } = useParams();
  const navigate = useNavigate();
  const goBack = useGoBack();
  const { access, profile } = useAuth();
  const { chats, setMuted, setArchived, live, closeChat, setLeoOff } = useChats();
  const { openSheet, closeSheet, toast } = useOverlay();
  const chat = chats.find((c) => c.id === id);
  const customerId = chat?.customerId;
  const { reminders } = useReminders(live);

  // What Leo did, the next booking and the last service (Gary, 18 Oct). Sample data in the preview.
  const [context, setContext] = useState<ChatContext | null>(null);
  const [tab, setTab] = useState<'info' | 'ask'>('info');
  // Customer, Booking and History over the chat (Gary, 18 Oct): the customer's Sentinel records, sample in the preview.
  const [record, setRecord] = useState<'customer' | 'bookings' | 'history' | null>(null);
  // The phone's Back steps back inside a panel, as its arrow does, and only closes it from the top level
  // (mobile review, 19 Oct: Back left Contact and threw away what was typed). One history step while a panel is
  // open; when Back is a step inside it, the step goes back in, as the panel is still open.
  const recordBack = useRef<RecordStep | null>(null);
  useBackStep('chatsRecord', record !== null, () => {
    const step = recordBack.current;
    if (!step) { setRecord(null); return; }
    if (step.stays()) window.history.pushState({ ...((window.history.state as Record<string, unknown> | null) ?? {}), chatsRecord: true }, '');
    step.back();
  });
  const leadId = chat?.leadId ?? null;
  // A lead with no customer yet gets New booking too (Gary, 19 Oct): booked from the lead's details.
  // A Peace of Sleep chat has no CleanMyBed bookings or services: its POS client and lead show from the context instead.
  const posChat = chat?.brand === 'pos';
  const api = useMemo(() => (!id || posChat ? null : live ? (customerId ? liveRecords(id, customerId) : leadId ? liveRecords(id, '', leadId) : null) : sampleRecords(id)), [id, live, customerId, leadId, posChat]);
  const [recCustomer, setRecCustomer] = useState<RecCustomer | null>(null);
  const rights = rightsFor(access.role === 'head_office', profile?.permissions ?? null);
  const [nextBooking, setNextBooking] = useState<RecBooking | null | 'loading'>('loading');
  const reloadBooking = useCallback(() => {
    if (!api) return;
    api.nextBooking().then(setNextBooking).catch(() => setNextBooking(null));
  }, [api]);
  useEffect(() => {
    setRecCustomer(null); setNextBooking('loading'); setRecord(null);
    if (!api) return;
    let gone = false;
    api.customer().then((x) => { if (!gone) setRecCustomer(x); }).catch(() => { if (!gone) setRecCustomer(null); });
    api.nextBooking().then((x) => { if (!gone) setNextBooking(x); }).catch(() => { if (!gone) setNextBooking(null); });
    return () => { gone = true; };
  }, [api]);
  useEffect(() => { setTab('info'); }, [id]);
  const [contextTick, setContextTick] = useState(0);
  useEffect(() => {
    if (!id) return;
    if (!live) { setContext(sampleContext(id)); return; }
    let gone = false;
    loadChatContext(id).then((x) => { if (!gone) setContext(x); }).catch(() => { if (!gone) setContext(null); });
    return () => { gone = true; };
  }, [id, live, contextTick]);


  if (!chat) return <Navigate to="/" replace />;
  const c = chat;
  const headOffice = access.role === 'head_office';

  // This customer's other conversations, by lead, customer or address (Gary, 19 Oct): email chats are separate
  // from their WhatsApp chat, so Info lists them all, each marked.
  const related = chats.filter((x) => x.id !== c.id && (
    (!!c.leadId && x.leadId === c.leadId) || (!!c.customerId && x.customerId === c.customerId) || (!!c.email && x.email === c.email)));
  const emailCount = related.filter((x) => x.email).length + (c.email ? 1 : 0);
  const conversations = () => openSheet(
    <>
      {/* Not a possessive made from the name (mobile review, 19 Oct: "Example:'s", "Harbour's", "+27's"). */}
      <h2 className="chats-sheet-title">Conversations</h2>
      <p className="chats-sheet-hint">Emails sent through Chats only.</p>
      {[c, ...related].map((x) => (
        <SheetOption
          key={x.id}
          icon={<Avatar name={x.name} colour={x.colour} small email={!!x.email} />}
          title={x.email ? `Email${x.subject ? `: ${x.subject}` : ''}` : 'WhatsApp chat'}
          sub={`${x.id === c.id ? 'This conversation' : x.archived ? 'Archived' : 'Open'} · ${x.time}`}
          onClick={() => { closeSheet(); if (x.id !== c.id) navigate(`/chat/${x.id}`); }}
        />
      ))}
    </>,
  );

  const messages = c.thread.filter(isMessage);
  const starred = messages.filter((m) => m.starred);
  // Each opens the chat at the latest one, as Starred does (mobile review, 19 Oct: the rows looked tappable and did nothing).
  const media = [
    { label: 'Photos', list: messages.filter((m) => m.body.kind === 'photo') },
    { label: 'Voice notes', list: messages.filter((m) => m.body.kind === 'voice') },
    { label: 'Links', list: messages.filter((m) => m.body.kind === 'link') },
  ];
  const phone = formatPhone(c.phone);

  // Asked first, as Delete is (mobile review, 19 Oct): one stray tap closed a live lead, and Chats has no way to undo it.
  const confirmClose = (outcome: 'booked' | 'closed') => openSheet(
    <>
      <h2 className="chats-sheet-title">{outcome === 'booked' ? 'Mark booked?' : 'Close this chat?'}</h2>
      <p className="chats-sheet-hint">{outcome === 'booked' ? 'The lead counts as booked. Chats cannot undo this.' : 'It moves to Archived. Sentinel keeps the record.'}</p>
      <div className="chats-pair">
        <button type="button" className="chats-btn chats-btn--ghost" onClick={closeSheet}>Cancel</button>
        <button type="button" className="chats-btn" onClick={() => { closeSheet(); closeChat(c.id, outcome); }}>{outcome === 'booked' ? 'Mark booked' : 'Close chat'}</button>
      </div>
    </>,
  );
  const toggleMute = () => { setMuted([c.id], !c.muted); toast(c.muted ? 'Alerts on for this chat' : 'Alerts off for this chat'); };
  // A result opens the chat at that message, highlighted (Gary, 15 Oct: search went nowhere).
  const openAt = (messageId: string) => navigate(`/chat/${c.id}?m=${encodeURIComponent(messageId)}`);
  const search = () => openSheet(<SearchSheet chat={c} onOpen={(messageId) => { closeSheet(); openAt(messageId); }} />);
  const sentinelPath = c.customerId || c.leadId ? sentinelPathFor(c) : null;
  // Junk, as the tracker (Gary, 19 Oct): an open lead chat only.
  // Every open customer chat (Gary, 19 Oct: Mikaylah's WhatsApp chat had no lead, so no Junk); with a lead it is
  // marked on the lead as the tracker does, without one the chat is noted and closed.
  // Reply time (Gary, 20 Oct): from the server live, sample waits in the preview.
  const replies = live ? context?.replies ?? null : sampleReplies(c.id);
  const canJunk = live && !c.isTeam && !c.closed && c.state !== 'booked';
  // Saved on the customer or lead, so a chat needs one; the chat's owner or head office (Gary, 20 Oct).
  const canLeoOff = !c.isTeam && c.brand === 'cmb' && (!live || ((!!c.customerId || !!c.leadId) && (headOffice || c.ownerId === profile?.id)));

  return (
    <div className={`chats-conv chats-contact${c.brand === 'pos' ? ' chats-brand-pos' : ''}`}>
      <header className="chats-topbar chats-topbar--conv">
        <button type="button" className="chats-icon-btn chats-back" aria-label="Back" onClick={() => goBack(`/chat/${c.id}`)}><BackIcon /></button>
        {/* "Contact", so the title does not repeat the Info tab under it (mobile review, 18 Oct). */}
        <h1 className="chats-topbar-title">{c.isTeam ? 'Info' : 'Contact'}</h1>
        {/* Which brand you answer as, always in the same place beside Info (Gary, 18 Oct): violet for
            CleanMyBed, gold for Peace of Sleep. */}
        {!c.isTeam && <span className="chats-contact-brand">{c.brand === 'pos' ? 'Peace of Sleep' : 'CleanMyBed'}</span>}
        {/* Alerts on or off, in sight in the header (Gary, 18 Oct: it was buried at the bottom). */}
        <button type="button" className={`chats-icon-btn chats-contact-mute${c.muted ? ' chats-contact-mute--off' : ''}`} onClick={toggleMute}
          aria-label={c.muted ? 'Alerts off for this chat. Turn on' : 'Alerts on for this chat. Turn off'} title={c.muted ? 'Alerts off. Tap to turn on' : 'Alerts on. Tap to turn off'}>
          {c.muted ? <BellOffIcon /> : <BellIcon />}
        </button>
      </header>
      {!c.isTeam && (
        <div className="chats-chips chats-contact-tabs" role="tablist">
          <button type="button" role="tab" aria-selected={tab === 'info'} className={`chats-chip${tab === 'info' ? ' active' : ''}`} onClick={() => setTab('info')}>Info</button>
          <button type="button" role="tab" aria-selected={tab === 'ask'} className={`chats-chip${tab === 'ask' ? ' active' : ''}`} onClick={() => setTab('ask')}>Ask&nbsp;<AiStaff /></button>
        </div>
      )}
      {tab === 'ask' && !c.isTeam ? (
        live ? (
          <DrcLive chatId={c.id} chips={ASK_CHIPS} placeholder={`Ask about ${c.name}`}
            // Copied is said only once it is (mobile review, 19 Oct).
            onUseReply={(line) => {
              const blocked = () => toast('Copying is blocked here. Press and hold the reply to copy it.');
              if (!navigator.clipboard) { blocked(); return; }
              navigator.clipboard.writeText(line).then(() => toast('Copied. Paste it in the chat.'), blocked);
            }}
            pageIntro={<p className="chats-sheet-hint chats-contact-ask-hint">Only you see this.</p>} />
        ) : (
          <main className="chats-screen"><p className="chats-empty">{STAFF_ASSISTANT_NAME} answers on live chats only.</p></main>
        )
      ) : (
      <main className="chats-screen chats-screen--grey">
        {/* Leo off for this customer, first in Info (Gary, 23 Oct). */}
        {canLeoOff && (
          <div className="chats-group">
            <button type="button" className="chats-set" onClick={() => setLeoOff(c.id, !c.leoOff)}>
              <div className="chats-set-main">Leo off for this customer</div>
              <Switch on={!!c.leoOff} label="Leo off for this customer" />
            </button>
          </div>
        )}
        {/* Search this chat, at the top as WhatsApp (Gary, 18 Oct). */}
        <div className="chats-searchrow chats-contact-search">
          <button type="button" className="chats-searchbox" onClick={search}>
            <SearchIcon />
            <span className="chats-searchbox-text">Search this chat</span>
          </button>
        </div>

        <div className="chats-contact-hero">
          <Avatar name={c.name} colour={c.colour} email={!!c.email} />
          <h2>{c.name}</h2>
          {/* The customer's status, as in the chat header but larger (Gary, 18 Oct). */}
          {!c.isTeam && <StatusChip chat={c} large />}
          {/* Tap to call, as WhatsApp's contact info (16 Oct). */}
          {phone && <p className="chats-contact-phone"><a className="chats-contact-link" href={`tel:${phone.replace(/[^\d+]/g, '')}`}>{phone}</a></p>}
          {/* Email chats (Gary, 19 Oct): the address and what the customer's last email was about. */}
          {c.email && <p className="chats-contact-phone"><a className="chats-contact-link" href={`mailto:${c.email}`}>{c.email}</a></p>}
          {c.subject && <p className="chats-sheet-hint">Subject: {c.subject}</p>}
        </div>

        {/* The customer's Sentinel records over the chat (Gary, 18 Oct): three buttons, each a panel. */}
        {!c.isTeam && api && recCustomer && (
          <div className={`chats-rec-buttons${recCustomer.id ? '' : ' chats-rec-buttons--one'}`}>
            {recCustomer.id && <button type="button" onClick={() => setRecord('customer')}><PersonIcon /><span>Customer</span></button>}
            {/* The next booking, else the latest cancelled one (Gary, 19 Oct), else one tap to book (18 Oct).
                Past ones are in History. */}
            <button type="button" className={nextBooking && nextBooking !== 'loading' && nextBooking.status !== 'cancelled' ? 'chats-rec-btn--on' : undefined} disabled={nextBooking === 'loading' || (!nextBooking && !rights.book)} onClick={() => setRecord('bookings')}>
              <CalendarIcon /><span>{nextBooking === 'loading' ? 'Booking' : nextBooking ? 'Booking' : 'New booking'}</span>
              <small>{nextBooking === 'loading' ? '…' : nextBooking ? `${nextBooking.status === 'cancelled' ? 'Cancelled ' : ''}${day(nextBooking.date)}` : 'None ahead'}</small>
            </button>
            {recCustomer.id && <button type="button" onClick={() => setRecord('history')}><ClockIcon /><span>History</span></button>}
          </div>
        )}
        {c.earlier && (
          <button type="button" className="chats-facts-link chats-rec-earlier" onClick={() => navigate(`/chat/${c.id}`)}>Earlier chats ({c.earlier.chats})</button>
        )}
        <RecordBack.Provider value={recordBack}>
        {api && recCustomer && record === 'customer' && (
          <CustomerPanel api={api} customer={recCustomer} canEdit={rights.editCustomer} onClose={() => setRecord(null)} onSaved={setRecCustomer}
            onOpenSentinel={headOffice && sentinelPath ? () => void (live ? openInSentinel(sentinelPath) : Promise.resolve(window.open(portalUrl(sentinelPath), '_blank'))) : undefined} />
        )}
        {api && recCustomer && record === 'bookings' && nextBooking !== 'loading' && (
          <BookingsPanel api={api} booking={nextBooking} customer={recCustomer} rights={rights} onClose={() => setRecord(null)} onChanged={reloadBooking}
            onLeadBooked={(m) => { setRecord(null); toast(m); }} />
        )}
        {api && recCustomer && record === 'history' && (
          <HistoryPanel api={api} name={[recCustomer.firstName, recCustomer.lastName].filter(Boolean).join(' ') || recCustomer.company || c.name} onClose={() => setRecord(null)}
            onCopied={() => { setRecord(null); toast('Payment link copied. Paste it in the chat.'); }} />
        )}
        </RecordBack.Provider>

        {/* From Leo (Gary, 18 Oct): always here on a CleanMyBed chat, one row the same height, and his
            hand-over, notes and quote open on a tap. Leo's own colour, as everywhere. */}
        {!c.isTeam && c.brand === 'cmb' && <LeoCard leo={context?.leo ?? null} replies={messages.filter((m) => m.assistant && m.from === 'out').length} />}

        {/* Peace of Sleep (3 Nov 2026): the POS client and its properties, or the POS lead, else Add to POS Leads. */}
        {!c.isTeam && c.brand === 'pos' && live && context?.pos && (() => {
          const { client, lead } = context.pos;
          const canAdd = !client && !lead && access.brands.includes('pos');
          return (
            <>
              {client && (
                <>
                  <p className="chats-group-title">Peace of Sleep client</p>
                  <div className="chats-group chats-facts">
                    <div className="chats-facts-pair"><span>Client</span><b>{client.name}</b></div>
                    {client.properties.map((p) => (
                      <div key={p.id} className="chats-facts-pair"><span>Property</span><b>{[p.name, p.town].filter(Boolean).join(', ')}</b></div>
                    ))}
                  </div>
                </>
              )}
              {lead && (
                <>
                  <p className="chats-group-title">POS lead</p>
                  <div className="chats-group chats-facts">
                    <div className="chats-facts-pair"><span>Property</span><b>{lead.property}</b></div>
                    {lead.region && <div className="chats-facts-pair"><span>Region</span><b>{lead.region}</b></div>}
                    {lead.rooms ? <div className="chats-facts-pair"><span>Rooms</span><b>{lead.rooms}</b></div> : null}
                    <div className="chats-facts-pair"><span>Status</span><b>{posWords(lead.status)}</b></div>
                    {lead.proposal && <div className="chats-facts-pair"><span>Proposal</span><b>{posWords(lead.proposal)}</b></div>}
                  </div>
                </>
              )}
              {canAdd && (
                <button type="button" className="chats-rec-row chats-info-labels"
                  onClick={() => openSheet(<PosLeadSheet chatId={c.id} onDone={(m) => { closeSheet(); toast(m); setContextTick((t) => t + 1); }} />)}>
                  <PersonIcon />
                  <span className="chats-rec-row-main"><span className="chats-info-none">Not on POS Leads</span></span>
                  <span className="chats-info-act">Add</span>
                </button>
              )}
            </>
          );
        })()}

        {c.lead && (
          <>
            <p className="chats-group-title">Enquiry</p>
            <div className="chats-group chats-facts">
              {c.lead.map((row) => (
                <div key={row.label} className="chats-facts-pair"><span>{row.label}</span><b>{row.value}</b></div>
              ))}
            </div>
          </>
        )}

        {/* Labels and owner, one compact card each (Gary, 18 Oct): the labels row opens the label sheet; the owner
            card says who, where and what state in two lines, with the chat decisions as buttons under it. */}
        {/* Emails (Gary, 19 Oct): every conversation with this customer, email and WhatsApp, one tap away. */}
        {!c.isTeam && (emailCount > 0 || related.length > 0) && (
          <button type="button" className="chats-rec-row chats-info-labels" onClick={conversations}>
            <MailIcon />
            <span className="chats-rec-row-main">{emailCount ? `Emails (${emailCount})` : 'Emails'}{related.some((x) => !x.email) ? ' · also on WhatsApp' : ''}</span>
            <span className="chats-info-act">Open</span>
          </button>
        )}
        <button type="button" className="chats-rec-row chats-info-labels" onClick={() => openSheet(<LabelSheet chat={c} />)}>
          <TagIcon />
          <span className="chats-rec-row-main">{c.labels?.length ? <LabelLine labels={c.labels} /> : <span className="chats-info-none">No labels</span>}</span>
          <span className="chats-info-act">{c.labels?.length ? 'Change' : 'Add'}</span>
        </button>

        {/* Reminder (Gary, 3 Nov 2026): Leo's or the team's, one tap to add, change, tick off or remove. */}
        {!c.isTeam && (c.customerId || c.leadId) && profile?.id && (() => {
          const rem = reminderFor(reminders, c);
          return (
            <button type="button" className="chats-rec-row chats-info-labels"
              onClick={() => openSheet(<ReminderSheet chat={c} reminder={rem} userId={profile.id} onDone={(m) => { closeSheet(); toast(m); }} />)}>
              <BellIcon />
              <span className="chats-rec-row-main">{rem ? <>Reminder {shortDate(rem.date)}<small>{rem.note ?? TYPE_WORDS[rem.type]}</small></> : <span className="chats-info-none">No reminder</span>}</span>
              <span className="chats-info-act">{rem ? 'Change' : 'Add'}</span>
            </button>
          );
        })()}

        <div className="chats-info-owner">
          {/* Owner and who is on duty live on the chat's top line; the owner is changed there (Gary, 23 Oct). */}
          {c.aiState === 'human_required' && <p className="chats-info-passed"><AiLeo /> passed it on: {handoverWords(c.aiHandover)}</p>}
          {(!c.isTeam || (headOffice && c.aiState === 'human')) && (
            <div className="chats-info-acts">
              {/* An operator only ever sees their own chats; the server allows the owner or head office (19 Oct). */}
              {!c.isTeam && c.state !== 'booked' && <button type="button" className="chats-chip" onClick={() => confirmClose('booked')}>Mark booked</button>}
              {!c.isTeam && !c.closed && <button type="button" className="chats-chip" onClick={() => confirmClose('closed')}>Close chat</button>}
              {canJunk && <button type="button" className="chats-chip" onClick={() => openSheet(<JunkSheet chatId={c.id} leadId={c.leadId ?? null} onDone={(m) => { closeSheet(); toast(m); }} />)}>Junk</button>}
            </div>
          )}
        </div>


        {/* Leo off for this customer (Gary, 20 Oct): the owner keeps them to themselves, in this chat and every later
            one. The owner or head office; the server checks again. Under it, how long they wait for a person. */}
        {!c.isTeam && replies && (
          <>
            <p className="chats-group-title">Replies</p>
            <div className="chats-group">
              {replies && (
                <button type="button" className="chats-set" disabled={!replies.length} onClick={() => openSheet(<ReplyTimeSheet waits={replies} />)}>
                  <div className="chats-set-main">Reply time<small>{replySummary(replies)}</small></div>
                </button>
              )}
            </div>
          </>
        )}

        <p className="chats-group-title">Media, links and docs</p>
        <div className="chats-group">
          {media.map(({ label, list }) => {
            const latest = list[list.length - 1];
            return latest ? (
              <button key={label} type="button" className="chats-set" onClick={() => openAt(latest.id)}>
                <div className="chats-set-main">{label}<small>{list.length}</small></div>
              </button>
            ) : (
              <div key={label} className="chats-set"><div className="chats-set-main">{label}<small>0</small></div></div>
            );
          })}
        </div>

        <p className="chats-group-title">Starred messages</p>
        <div className="chats-group">
          {starred.length === 0 && <div className="chats-set"><div className="chats-set-main"><small>None</small></div></div>}
          {starred.map((m) => (
            <button key={m.id} type="button" className="chats-set" onClick={() => openAt(m.id)}>
              <span className="chats-row-flag"><StarIcon filled /></span>
              <div className="chats-set-main">{previewOf(m.body)}<small>{m.at}</small></div>
            </button>
          ))}
        </div>

        <div className="chats-group">
          <button type="button" className="chats-set" onClick={() => { setArchived([c.id], !c.archived); toast(c.archived ? 'Chat unarchived' : 'Chat archived'); navigate(c.archived ? `/chat/${c.id}` : '/'); }}>
            <span className="chats-row-flag">{c.archived ? <UnarchiveIcon /> : <ArchiveIcon />}</span>
            <div className="chats-set-main">{c.archived ? 'Unarchive chat' : 'Archive chat'}</div>
          </button>
        </div>
      </main>
      )}
    </div>
  );
}

const ASK_CHIPS = ['Is this an existing customer?', 'What have we done for them before?', 'Any open bookings or quotes?', 'Any unpaid invoices?'];

/** "Beds: Large" reads as "Large bed", "Pillows : Small" as "Small pillow"; other names as they are. */
function itemName(item: string): string {
  const m = /^\s*(\w+)\s*:\s*(.+)$/.exec(item);
  if (!m) return item;
  return `${m[2]!.trim()} ${m[1]!.toLowerCase().replace(/s$/, '')}`;
}

/** Leo's part of the chat: a fixed one-line summary, the details on a tap. */
function LeoCard({ leo, replies }: { leo: ChatContext['leo'] | null; replies: number }) {
  const [open, setOpen] = useState(false);
  const notes = leo?.notes.length ?? 0;
  const details = !!(leo?.handover || notes || leo?.quote);
  // What Leo did, in a line (Gary, 18 Oct: a chat Leo only talked in read "Nothing yet", as if broken).
  const parts = [
    replies ? `${replies} repl${replies === 1 ? 'y' : 'ies'}` : null,
    leo?.handover ? 'Handed over' : null,
    notes ? `${notes} note${notes > 1 ? 's' : ''}` : null,
    leo?.quote ? `Estimate ${formatRand(leo.quote.total)}` : null,
  ].filter(Boolean);
  return (
    <div className="chats-group chats-leo-card">
      <button type="button" className="chats-leo-card-head" aria-expanded={open} disabled={!details} onClick={() => setOpen(!open)}>
        <span className="chats-leo-card-title">From <AiLeo /></span>
        <span className="chats-leo-card-sum">{parts.length ? parts.join(' · ') : 'Not in this chat yet'}</span>
        {details && <span className={`chats-leo-card-chev${open ? ' chats-leo-card-chev--open' : ''}`}><ArrowDownIcon /></span>}
      </button>
      {/* Each item as a label over its text, easy to read in a narrow panel. */}
      {open && leo && (
        <div className="chats-leo-card-body">
          {leo.handover && <div className="chats-leo-item"><span>Handed over</span><p>{leo.handover}</p></div>}
          {leo.notes.map((n) => <div key={n} className="chats-leo-item"><span>Note for the team</span><p>{n}</p></div>)}
          {leo.quote && (
            <div className="chats-leo-item">
              <span>Estimate{leo.quote.minimum ? ' (minimum visit)' : ''}</span>
              {leo.quote.lines.map((l) => <p key={l.item} className="chats-leo-line"><span>{l.quantity} × {itemName(l.item)}</span><b>{formatRand(l.line_total)}</b></p>)}
              {leo.quote.minimum && leo.quote.total > leo.quote.lines.reduce((s, l) => s + l.line_total, 0) && (
                <p className="chats-leo-line"><span>Up to the minimum visit</span><b>{formatRand(leo.quote.total - leo.quote.lines.reduce((s, l) => s + l.line_total, 0))}</b></p>
              )}
              <p className="chats-leo-line chats-leo-line--total"><span>Total</span><b>{formatRand(leo.quote.total)}</b></p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const mins = (m: number) => (m < 60 ? `${m} min` : `${Math.floor(m / 60)} h ${m % 60} min`);

/** "Typical 14 min · 1 of 4 within 10 min": the middle wait, and how many were answered inside the 10 minute limit. */
function replySummary(waits: ReplyWait[]): string {
  if (!waits.length) return 'No waits in the last 30 days';
  const now = waits.find((w) => w.waiting && w.minutes >= 10);
  const done = waits.filter((w) => !w.waiting);
  const sorted = done.map((w) => w.minutes).sort((a, b) => a - b);
  const parts = [
    now ? `Waiting ${mins(now.minutes)} now` : null,
    sorted.length ? `Typical ${mins(sorted[Math.floor((sorted.length - 1) / 2)]!)}` : null,
    done.length ? `${done.filter((w) => w.minutes <= 10).length} of ${done.length} within 10 min` : null,
  ].filter(Boolean);
  return parts.length ? parts.join(' · ') : 'No waits in the last 30 days';
}

const waitWhen = (at: string) => new Date(at).toLocaleString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false });

/** Each wait, newest first: how long, when the customer wrote, who replied. */
function ReplyTimeSheet({ waits }: { waits: ReplyWait[] }) {
  return (
    <>
      <h2 className="chats-sheet-title">Reply time</h2>
      <p className="chats-sheet-hint">Last 30 days, working hours.</p>
      {waits.map((w) => (
        <div key={w.at} className="chats-set">
          <div className="chats-set-main">
            {w.waiting ? `Waiting ${mins(w.minutes)}` : mins(w.minutes)}
            <small>{waitWhen(w.at)}{w.by ? ` · ${w.by}` : ''}{w.covered ? <>{' · '}<AiLeo /> covered at 20 min</> : null}</small>
          </div>
        </div>
      ))}
    </>
  );
}

/** A POS status as words: "proposal_sent" reads "Proposal sent". */
function posWords(status: string): string {
  const t = status.replace(/_/g, ' ').trim();
  return t ? t[0]!.toUpperCase() + t.slice(1) : status;
}
